import React, { useState, useEffect } from 'react';
import {
  CreditCard, Check, X, Save, Shield, Eye, EyeOff, Sparkles, Zap,
  Crown, Star, Palette, Settings, Search, LayoutGrid
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// All DVC Templates - Must match frontend DigitalCard.js
const ALL_TEMPLATES = [
  // Metaverse Templates
  { id: 'metaverse-neon', name: 'Metaverse Neon', category: 'metaverse', color: '#00FFFF', 
    description: 'Cyberpunk-inspired with glowing effects', isPremium: true },
  { id: 'cyber-punk', name: 'Cyber Punk', category: 'metaverse', color: '#FF00FF',
    description: 'Pink/purple with glitch effects', isPremium: true },
  { id: 'hologram', name: 'Hologram 3D', category: 'metaverse', color: '#00FF88',
    description: 'Green holographic with scanning lines', isPremium: true },
  { id: 'matrix-code', name: 'Matrix Code', category: 'metaverse', color: '#00FF00',
    description: 'Green digital rain animation', isPremium: true },
  { id: 'quantum', name: 'Quantum Flux', category: 'metaverse', color: '#8B5CF6',
    description: 'Purple with wave animations', isPremium: true },
  { id: 'neon-grid', name: 'Neon Grid', category: 'metaverse', color: '#FF3366',
    description: 'Retro neon grid aesthetic', isPremium: true },
  { id: 'synthwave', name: 'Synthwave', category: 'metaverse', color: '#FF6EC7',
    description: '80s retro-futuristic vibes', isPremium: true },
  { id: 'virtual-reality', name: 'Virtual Reality', category: 'metaverse', color: '#00E5FF',
    description: 'VR-inspired digital design', isPremium: true },
  
  // Luxury Templates
  { id: 'gold-luxury', name: 'Gold Luxury', category: 'luxury', color: '#FFD700',
    description: 'Premium gold with shimmer effects', isPremium: true },
  { id: 'diamond', name: 'Diamond Elite', category: 'luxury', color: '#B9F2FF',
    description: 'White/cyan luxury design', isPremium: true },
  { id: 'carbon-fiber', name: 'Carbon Fiber', category: 'luxury', color: '#FFFFFF',
    description: 'Dark minimal premium', isPremium: true },
  { id: 'platinum', name: 'Platinum Premium', category: 'luxury', color: '#E5E4E2',
    description: 'Sleek platinum elegance', isPremium: true },
  { id: 'rose-gold', name: 'Rose Gold', category: 'luxury', color: '#E8B4B8',
    description: 'Warm rose gold tones', isPremium: true },
  { id: 'emerald', name: 'Emerald Royal', category: 'luxury', color: '#50C878',
    description: 'Rich emerald green', isPremium: true },
  
  // Nature & Cosmic
  { id: 'aurora', name: 'Aurora Borealis', category: 'cosmic', color: '#7CFF7C',
    description: 'Northern lights inspired', isPremium: true },
  { id: 'galaxy', name: 'Galaxy Stars', category: 'cosmic', color: '#E0B0FF',
    description: 'Cosmic purple/pink gradients', isPremium: true },
  { id: 'sunset-vibes', name: 'Sunset Vibes', category: 'cosmic', color: '#FF6B6B',
    description: 'Warm orange/rose gradients', isPremium: false },
  { id: 'nebula', name: 'Cosmic Nebula', category: 'cosmic', color: '#C084FC',
    description: 'Deep space nebula colors', isPremium: true },
  { id: 'northern-lights', name: 'Northern Lights', category: 'cosmic', color: '#4ADE80',
    description: 'Green aurora effect', isPremium: true },
  { id: 'fire-storm', name: 'Fire Storm', category: 'cosmic', color: '#FF4500',
    description: 'Intense fire gradients', isPremium: false },
  
  // Classic Templates
  { id: 'default', name: 'Classic Gold', category: 'classic', color: '#D4AF37',
    description: 'Traditional premium style', isPremium: false },
  { id: 'tech-cyan', name: 'Tech Cyan', category: 'tech', color: '#00D9FF',
    description: 'Blue tech with network nodes', isPremium: false },
  { id: 'ocean', name: 'Deep Ocean', category: 'classic', color: '#06B6D4',
    description: 'Calming blue waves', isPremium: false },
  { id: 'royal', name: 'Royal Purple', category: 'classic', color: '#A855F7',
    description: 'Elegant purple theme', isPremium: false },
  { id: 'midnight', name: 'Midnight Blue', category: 'classic', color: '#3B82F6',
    description: 'Deep midnight aesthetic', isPremium: false },
  { id: 'forest', name: 'Forest Green', category: 'classic', color: '#22C55E',
    description: 'Natural forest vibes', isPremium: false },
];

const CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: LayoutGrid },
  { id: 'metaverse', name: 'Metaverse', icon: Zap },
  { id: 'luxury', name: 'Luxury', icon: Crown },
  { id: 'cosmic', name: 'Cosmic', icon: Star },
  { id: 'tech', name: 'Tech', icon: Settings },
  { id: 'classic', name: 'Classic', icon: CreditCard },
];

const DVCTemplateAdmin = () => {
  const [settings, setSettings] = useState({
    enabled_templates: ALL_TEMPLATES.map(t => t.id),
    premium_only_templates: ALL_TEMPLATES.filter(t => t.isPremium).map(t => t.id),
    default_template: 'default',
    require_activation: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/dvc/template-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // If endpoint doesn't exist yet, use defaults
      console.log('Using default DVC template settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/dvc/template-settings', settings);
      toast.success('DVC template settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = (templateId) => {
    setSettings(prev => ({
      ...prev,
      enabled_templates: prev.enabled_templates.includes(templateId)
        ? prev.enabled_templates.filter(id => id !== templateId)
        : [...prev.enabled_templates, templateId]
    }));
  };

  const togglePremium = (templateId) => {
    setSettings(prev => ({
      ...prev,
      premium_only_templates: prev.premium_only_templates.includes(templateId)
        ? prev.premium_only_templates.filter(id => id !== templateId)
        : [...prev.premium_only_templates, templateId]
    }));
  };

  const enableAllInCategory = (category) => {
    const categoryTemplates = category === 'all' 
      ? ALL_TEMPLATES.map(t => t.id)
      : ALL_TEMPLATES.filter(t => t.category === category).map(t => t.id);
    setSettings(prev => ({
      ...prev,
      enabled_templates: [...new Set([...prev.enabled_templates, ...categoryTemplates])]
    }));
  };

  const disableAllInCategory = (category) => {
    const categoryTemplates = category === 'all'
      ? ALL_TEMPLATES.map(t => t.id)
      : ALL_TEMPLATES.filter(t => t.category === category).map(t => t.id);
    setSettings(prev => ({
      ...prev,
      enabled_templates: prev.enabled_templates.filter(id => !categoryTemplates.includes(id))
    }));
  };

  const filteredTemplates = ALL_TEMPLATES.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="dvc-template-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              Digital Visiting Card Templates
            </h1>
            <p className="text-muted-foreground mt-1">
              Control which DVC templates are available to users
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-primary to-amber-500 text-primary-foreground rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50 hover:shadow-lg hover:shadow-primary/30 transition-all"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save Changes
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-500">{settings.enabled_templates.length}</p>
                <p className="text-sm text-muted-foreground">Enabled Templates</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{settings.premium_only_templates.length}</p>
                <p className="text-sm text-muted-foreground">Premium Only</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-500">{ALL_TEMPLATES.filter(t => t.category === 'metaverse').length}</p>
                <p className="text-sm text-muted-foreground">Metaverse Templates</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-500">{ALL_TEMPLATES.length}</p>
                <p className="text-sm text-muted-foreground">Total Templates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => enableAllInCategory(selectedCategory)}
            className="px-4 py-2 bg-green-500/20 text-green-500 rounded-xl text-sm font-medium hover:bg-green-500/30 transition-all"
          >
            Enable All {selectedCategory !== 'all' ? CATEGORIES.find(c => c.id === selectedCategory)?.name : ''}
          </button>
          <button
            onClick={() => disableAllInCategory(selectedCategory)}
            className="px-4 py-2 bg-red-500/20 text-red-500 rounded-xl text-sm font-medium hover:bg-red-500/30 transition-all"
          >
            Disable All {selectedCategory !== 'all' ? CATEGORIES.find(c => c.id === selectedCategory)?.name : ''}
          </button>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates.map(template => {
            const isEnabled = settings.enabled_templates.includes(template.id);
            const isPremiumOnly = settings.premium_only_templates.includes(template.id);

            return (
              <div
                key={template.id}
                className={`relative rounded-2xl overflow-hidden border-2 transition-all ${
                  isEnabled 
                    ? 'border-green-500/50 bg-green-500/5' 
                    : 'border-white/10 bg-white/[0.02] opacity-60'
                }`}
              >
                {/* Color Preview */}
                <div 
                  className="h-24 relative"
                  style={{ 
                    background: `linear-gradient(135deg, ${template.color}40, ${template.color}10)`,
                  }}
                >
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, ${template.color}30, transparent 70%)`
                    }}
                  />
                  {/* Category Badge */}
                  <span className="absolute top-2 left-2 px-2 py-1 bg-black/40 backdrop-blur-sm rounded-lg text-xs font-medium capitalize">
                    {template.category}
                  </span>
                  {/* Premium Badge */}
                  {isPremiumOnly && (
                    <span className="absolute top-2 right-2 px-2 py-1 bg-amber-500/90 rounded-lg text-xs font-bold flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: template.color }}
                    />
                    <h3 className="font-bold">{template.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{template.description}</p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleTemplate(template.id)}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                        isEnabled
                          ? 'bg-green-500 text-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      {isEnabled ? (
                        <>
                          <Eye className="w-4 h-4" />
                          Enabled
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Disabled
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => togglePremium(template.id)}
                      className={`px-3 py-2 rounded-xl transition-all ${
                        isPremiumOnly
                          ? 'bg-amber-500 text-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title={isPremiumOnly ? 'Premium Only' : 'Available to All'}
                    >
                      <Crown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Default Template Setting */}
        <div className="glass-card p-6 mt-8">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Default Template
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Select the default template for new users
          </p>
          <select
            value={settings.default_template}
            onChange={(e) => setSettings(prev => ({ ...prev, default_template: e.target.value }))}
            className="w-full max-w-md px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
          >
            {ALL_TEMPLATES.filter(t => settings.enabled_templates.includes(t.id)).map(template => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.category})
              </option>
            ))}
          </select>
        </div>

        {/* Member Permissions & Limits */}
        <div className="glass-card p-6 mt-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            Member Permissions & Limits
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Max Social Links */}
            <div>
              <label className="block text-sm font-medium mb-2">Max Social Links per Card</label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.max_social_links || 10}
                onChange={(e) => setSettings(prev => ({ ...prev, max_social_links: parseInt(e.target.value) }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
              />
              <p className="text-xs text-muted-foreground mt-1">Limit social links for non-premium members</p>
            </div>

            {/* Max Custom Links */}
            <div>
              <label className="block text-sm font-medium mb-2">Max Custom Links per Card</label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.max_custom_links || 5}
                onChange={(e) => setSettings(prev => ({ ...prev, max_custom_links: parseInt(e.target.value) }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
              />
              <p className="text-xs text-muted-foreground mt-1">Limit custom links for non-premium members</p>
            </div>

            {/* Floating Icons Toggle */}
            <div>
              <label className="block text-sm font-medium mb-2">Floating Social Icons</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSettings(prev => ({ ...prev, enable_floating_icons: !prev.enable_floating_icons }))}
                  className={`relative w-14 h-7 rounded-full transition-all ${
                    settings.enable_floating_icons !== false ? 'bg-green-500' : 'bg-white/20'
                  }`}
                >
                  <div 
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${
                      settings.enable_floating_icons !== false ? 'left-8' : 'left-1'
                    }`}
                  />
                </button>
                <span className="text-sm">{settings.enable_floating_icons !== false ? 'Enabled' : 'Disabled'}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Enable stunning floating icons for metaverse templates</p>
            </div>

            {/* Animation Speed */}
            <div>
              <label className="block text-sm font-medium mb-2">Animation Speed</label>
              <select
                value={settings.animation_speed || 'normal'}
                onChange={(e) => setSettings(prev => ({ ...prev, animation_speed: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
              >
                <option value="slow">Slow</option>
                <option value="normal">Normal</option>
                <option value="fast">Fast</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Control floating icon animation speed</p>
            </div>
          </div>

          {/* Feature Access Matrix */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="font-semibold mb-4">Feature Access by User Type</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4">Feature</th>
                    <th className="text-center py-3 px-4">Free Members</th>
                    <th className="text-center py-3 px-4">Activated Members</th>
                    <th className="text-center py-3 px-4">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4">Metaverse Templates</td>
                    <td className="text-center py-3 px-4">
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, metaverse_free: !prev.metaverse_free }))}
                        className={`w-6 h-6 rounded flex items-center justify-center ${settings.metaverse_free ? 'bg-green-500' : 'bg-white/10'}`}
                      >
                        {settings.metaverse_free && <Check className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4">Floating Social Icons</td>
                    <td className="text-center py-3 px-4">
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, floating_icons_free: !prev.floating_icons_free }))}
                        className={`w-6 h-6 rounded flex items-center justify-center ${settings.floating_icons_free ? 'bg-green-500' : 'bg-white/10'}`}
                      >
                        {settings.floating_icons_free && <Check className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4">Luxury Templates</td>
                    <td className="text-center py-3 px-4">
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, luxury_free: !prev.luxury_free }))}
                        className={`w-6 h-6 rounded flex items-center justify-center ${settings.luxury_free ? 'bg-green-500' : 'bg-white/10'}`}
                      >
                        {settings.luxury_free && <Check className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4">Profile Image Upload</td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Unlimited Links</td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center mx-auto">
                        <X className="w-4 h-4 text-red-400" />
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, unlimited_activated: !prev.unlimited_activated }))}
                        className={`w-6 h-6 rounded flex items-center justify-center ${settings.unlimited_activated ? 'bg-green-500' : 'bg-white/10'}`}
                      >
                        {settings.unlimited_activated ? <Check className="w-4 h-4" /> : <X className="w-4 h-4 text-red-400" />}
                      </button>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center mx-auto">
                        <Check className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>Enabled for Users</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500" />
            <span>Premium/Activated Only</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-white/20" />
            <span>Disabled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DVCTemplateAdmin;
